import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSettings } from '../../contexts/SettingsContext';

/**
 * DonutChart - SVG-based donut chart with legend
 * Supports dark/light mode via theme context
 * Uses dynamic theme colors, no hardcoded colors
 */

// Color palette that harmonizes with primary/secondary/accent
const CHART_PALETTE = [
  '#3B5BDB', '#20C997', '#845EF7', '#F06595', '#FF922B',
  '#228BE6', '#12B886', '#BE4BDB', '#FA5252', '#FAB005',
  '#15AABF', '#40C057', '#7950F2', '#E64980', '#FD7E14',
];

const DonutChart = ({
  data = [],
  title = '',
  size = 200,
  strokeWidth = 32,
  showLegend = true,
  maxItems = 8,
  className = '',
}) => {
  const { settings } = useSettings();
  const primaryColor = settings?.theme?.colors?.primary || '#3B5BDB';

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Sort by count descending, take top items
    const sorted = [...data].sort((a, b) => b.count - a.count);
    const topItems = sorted.slice(0, maxItems);

    // If there are remaining items, group as "Other"
    if (sorted.length > maxItems) {
      const otherCount = sorted.slice(maxItems).reduce((sum, item) => sum + item.count, 0);
      topItems.push({ name: 'Other', count: otherCount });
    }

    const total = topItems.reduce((sum, item) => sum + item.count, 0);
    if (total === 0) return [];

    let currentAngle = 0;
    return topItems.map((item, index) => {
      const percentage = (item.count / total) * 100;
      const angle = (item.count / total) * 360;
      const startAngle = currentAngle;
      currentAngle += angle;

      return {
        ...item,
        percentage: Math.round(percentage * 10) / 10,
        color: CHART_PALETTE[index % CHART_PALETTE.length],
        startAngle,
        endAngle: currentAngle,
      };
    });
  }, [data, maxItems]);

  const total = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.count, 0);
  }, [chartData]);

  // SVG arc path calculation
  const getArcPath = (startAngle, endAngle, radius, cx, cy) => {
    // Clamp angles to avoid rendering issues
    const clampedEnd = Math.min(endAngle, startAngle + 359.99);

    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((clampedEnd - 90) * Math.PI) / 180;

    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);

    const largeArc = clampedEnd - startAngle > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Animation state
  const [animated, setAnimated] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setAnimated(false);
    timerRef.current = requestAnimationFrame(() => {
      timerRef.current = requestAnimationFrame(() => {
        setAnimated(true);
      });
    });
    return () => {
      if (timerRef.current) cancelAnimationFrame(timerRef.current);
    };
  }, [chartData]);

  // Compute stroke-dasharray and dashoffset for each arc
  const arcAnimations = useMemo(() => {
    return chartData.map((item) => {
      const angle = item.endAngle - item.startAngle;
      const arcLength = (angle / 360) * circumference;
      return { dashArray: `${arcLength} ${circumference}`, arcLength };
    });
  }, [chartData, circumference]);

  if (!chartData.length) {
    return (
      <div className={`flex flex-col items-center justify-center p-6 ${className}`}>
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
          <svg className="w-8 h-8 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
          </svg>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">No data available</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      {title && (
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 uppercase tracking-wider">
          {title}
        </h3>
      )}

      <div className="flex flex-col lg:flex-row items-center gap-6">
        {/* SVG Donut */}
        <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {/* Background circle */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              className="stroke-gray-100 dark:stroke-gray-800"
              strokeWidth={strokeWidth}
            />

            {/* Data arcs */}
            {chartData.map((item, index) => {
              const anim = arcAnimations[index];
              return (
                <path
                  key={index}
                  d={getArcPath(item.startAngle, item.endAngle, radius, center, center)}
                  fill="none"
                  stroke={item.color}
                  strokeWidth={strokeWidth}
                  strokeLinecap="butt"
                  className="hover:opacity-80"
                  style={{
                    strokeDasharray: anim.dashArray,
                    strokeDashoffset: animated ? 0 : anim.arcLength,
                    transition: `stroke-dashoffset ${0.6 + index * 0.1}s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.08}s`,
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
                  }}
                />
              );
            })}
          </svg>

          {/* Center text */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center"
            style={{
              opacity: animated ? 1 : 0,
              transform: animated ? 'scale(1)' : 'scale(0.6)',
              transition: 'opacity 0.5s ease-out 0.3s, transform 0.5s ease-out 0.3s',
            }}
          >
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{total}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">Total</span>
          </div>
        </div>

        {/* Legend */}
        {showLegend && (
          <div className="flex-1 grid grid-cols-1 gap-2 min-w-0">
            {chartData.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                style={{
                  opacity: animated ? 1 : 0,
                  transform: animated ? 'translateX(0)' : 'translateX(-12px)',
                  transition: `opacity 0.4s ease-out ${0.2 + index * 0.06}s, transform 0.4s ease-out ${0.2 + index * 0.06}s`,
                }}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                  {item.name}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {item.count}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    ({item.percentage}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DonutChart;
